// CLI v0.8 — `cloudgrid rollback [<name>] [--revision N]`.
//
// Spec v17 §13.5: roll back to a previous successful deploy. Server-side
// re-points the entity at an existing built image — no rebuild. Container
// images only; env vars and secrets are not affected (legacy CLI carried
// the same warning, retained per audit Part B.4 #1).
//
// Confirmation prompt fires by default. `--yes` skips it; non-TTY without
// `--yes` errors fast (CONFIRMATION_REQUIRED) so CI doesn't hang.

import { Command } from 'commander';
import inquirer from 'inquirer';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { resolveEntityTarget } from '../services/resolve-entity-target.js';

export interface RunRollbackOpts {
  client: IV2Client;
  name: string | undefined;
  yes?: boolean;
  revision?: number;
  /** ADR 036 V3 — roll back to a specific version_id (not just "previous"). */
  to?: string;
  cwd?: string;
  /** Override stdin TTY detection — for tests and non-interactive shells. */
  isTTY?: boolean;
  /**
   * Confirmation function. Returns `true` to proceed, `false` to cancel.
   * Defaults to inquirer; tests inject their own.
   */
  promptFn?: (message: string) => Promise<boolean>;
}

/**
 * Body the v2 rollback endpoint accepts.
 *
 * `revision` is the spec v17 §13.5 contract:
 *   * positive integer → absolute revision number;
 *   * negative integer → N-back-from-current (default −1).
 *
 * Omitting `revision` is also valid — the server picks the most recent
 * successful deploy before the current one, matching the unflagged spec
 * default.
 */
interface RollbackRequestBody { revision?: number; version_id?: string }

interface RollbackResponse {
  entity_id: string;
  /** Present on the legacy "previous deploy" path. */
  revision?: number;
  /** Present on the ADR 036 V3 version-addressed path. */
  version_id?: string;
  status: string;
}

export async function runRollback(opts: RunRollbackOpts): Promise<void> {
  const cwd = opts.cwd ?? process.cwd();
  // ADR 036 V2: the rollback endpoint resolves the entity globally by
  // membership, so the link path skips the active-org pre-check.
  const target = await resolveEntityTarget(opts.client, opts.name, cwd, {
    skipLinkOrgCheck: true,
    bareSlugAcrossOrgs: true,
    verb: 'rollback',
  });

  if (!opts.yes) {
    const isTTY = opts.isTTY ?? Boolean(process.stdin.isTTY);
    if (!isTTY) {
      throw new V2ApiError(
        400,
        'CONFIRMATION_REQUIRED',
        `Rollback needs confirmation. Re-run with --yes in non-interactive shells.`,
      );
    }
    const dest = opts.to ? `version ${opts.to}` : 'the previous successful deploy';
    process.stdout.write(
      `Rollback only re-points ${target.name} at ${opts.to ? "that version's" : 'the previous'} container images.\n` +
      `Env vars and secrets are not affected.\n`,
    );
    const ok = await (opts.promptFn ?? defaultConfirm)(
      `Roll back ${target.name} to ${dest}?`,
    );
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  const body: RollbackRequestBody = {};
  // `--to <version_id>` takes precedence over the legacy `--revision`.
  if (opts.to) body.version_id = opts.to;
  else if (typeof opts.revision === 'number') body.revision = opts.revision;

  const resp = await opts.client.post<RollbackResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/rollback`,
    body,
  );
  if (resp.version_id) {
    process.stdout.write(`Rolling ${target.name} back to version ${resp.version_id}.\n`);
    return;
  }
  const revLabel = describeRevision(resp.revision ?? -1);
  process.stdout.write(`Rolling ${target.name} back ${revLabel}.\n`);
}

function describeRevision(rev: number): string {
  if (Number.isFinite(rev) && rev < 0) {
    const n = Math.abs(rev);
    return n === 1 ? 'to the previous deploy' : `${n} deploys`;
  }
  if (Number.isFinite(rev) && rev > 0) return `to revision ${rev}`;
  return 'to the previous deploy';
}

async function defaultConfirm(message: string): Promise<boolean> {
  const { ok } = await inquirer.prompt([
    { type: 'confirm', name: 'ok', message, default: false },
  ]);
  return Boolean(ok);
}

export const rollbackCommand: CommanderCommand = new Command('rollback')
  .description('Roll back to a previous successful deploy (re-points images; env/secrets unchanged).')
  .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
  .option('--revision <n>', 'Revision number (positive: absolute; negative: N-back-from-current)', (v) => {
    const n = Number.parseInt(v, 10);
    if (!Number.isFinite(n) || n === 0) {
      throw new Error(`--revision must be a non-zero integer, got '${v}'`);
    }
    return n;
  })
  .option('--to <version_id>', 'Roll back to a specific version id (ADR 036 V3)')
  .option('-y, --yes', 'Skip the confirmation prompt')
  .action(async (name: string | undefined, opts: { yes?: boolean; revision?: number; to?: string }) => {
    try {
      const client = await getV2Client();
      await runRollback({ client, name, yes: opts.yes, revision: opts.revision, to: opts.to });
    } catch (err) {
      await handleV2Error(err);
    }
  });
